import React, { useState } from 'react'
import Summary from '../sections/summary'
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js'
import debounce from 'lodash.debounce'
import { ajaxPost } from '../../../ajax'

export default function PaymentForm(props) {
  const { token, isLoading, alert, autoCharge, currency, totalAmount, totalFee, onPayment } = props
  const stripe = useStripe()
  const elements = useElements()
  const [complete, setComplete] = useState({})
  const [error, setError] = useState(null)
  const [processing, setProcessing] = useState(isLoading)
  const [brand, setBrand] = useState('unknown')
  const formCompleted = complete.card && complete.date && complete.cvc
  const currencyName = currency == 'usd' ? '' : ` (${currency.toUpperCase()})`
  const options = {
    style: {
      base: {
        fontWeight: 'lighter',
        fontSize: '16px',
      },
    },
  }

  const createIntent = async () => {
    const params = {
      token: token,
      currency: currency,
      amount: totalAmount.toFixed(2),
      app_fee: totalFee.toFixed(2)
    }
    const response = await ajaxPost('/plugin/payment/create_intent', params)
    const json = await response.json()
    return json.intent
  }

  const onSubmit = (e) => {
    e.preventDefault()
    startProcessing()
    debouncedSubmit()
  }

  const handleSubmit = async () => {
    if (!stripe || !elements) return

    autoCharge ? processPayment() : storePayment()
  }

  const processPayment = async () => {
    const intent = await createIntent()
    if (intent.error) {
      onError(intent.error)
      return
    }

    if (intent.secret == 'pi_test_valid') {
      return paymentSuccess({type: 'card_number', token: intent.secret})
    }

    const result = await stripe.confirmCardPayment(intent.secret, {
      payment_method: { card: elements.getElement(CardNumberElement) }
    })

    if (result.error) {
      onError(result.error.message)
    } else {
      if (result.paymentIntent.status === 'requires_capture') {
        paymentSuccess(result.paymentIntent.id)
      }
    }
  }

  const storePayment = async () => {
    const result = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardNumberElement)
    })

    if (result.error)
      onError(result.error.message)
    else
      paymentSuccess(result.paymentMethod.id)
  }

  const paymentSuccess = (nonce) => {
    stopProcessing()
    onPayment({nonce: nonce})
  }

  const onError = (error) => {
    setError(error)
    setProcessing(false)
  }

  const startProcessing = () => {
    setError(null)
    setProcessing(true)
  }

  const stopProcessing = () => {
    setError(null)
    setProcessing(false)
  }

  const debouncedSubmit = debounce(handleSubmit, 500)

  const onElementChange = (event, name) => {
    if (name == 'card') setBrand(event.brand)
    setComplete({...complete, [name]: event.complete})
  }

  const submitDisabled = !stripe || !formCompleted || processing || isLoading

  return (
    <form className={`stripe-form ${processing ? ' isLoading' : ''}`} onSubmit={onSubmit}>
      { error && <label className='stripe-error'>{error}</label> }
      { alert && <label className='stripe-error'>{alert.text}</label> }
      <div className='stripe-form-card-number'>
        { (processing || isLoading) && <div className='animated-spinner'></div> }
        <label className='stripe-label' htmlFor='cardnumber'>
          Card Number
          <div className='stripe-card-input'>
            <CardNumberElement options={options} onChange={(event) => onElementChange(event, 'card')} />
            <div className={'brand-img' + ` ${brand}-img`}></div>
          </div>
        </label>
        <div className='stripe-form-expiration-cvc'>
          <label className='stripe-label' htmlFor='exp-date'>
            EXP
            <CardExpiryElement options={options} onChange={(event) => onElementChange(event, 'date')} />
          </label>
          <label className='stripe-label' htmlFor='cvc'>
            CVV
            <CardCvcElement options={options} onChange={(event) => onElementChange(event, 'cvc')} />
          </label>
        </div>
      </div>
      <Summary
        mobile
        inline
        step={props.step}
        price={props.price}
        totalAmount={totalAmount}
        totalFee={totalFee}
        fieldSizes={props.fieldSizes}
        selectedSize={props.selectedSize}
        selectedDate={props.selectedDate}
        selectedDuration={props.selectedDuration}
        selectedInterval={props.selectedInterval}
      />
      <button className='btn btn-next' type='submit' disabled={submitDisabled}>
        {`Confirm and Pay $${totalAmount.toFixed(2)}${currencyName}`}
      </button>
    </form>
  )
}
